/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

// A form as the builder saves it: one JSON file per form in
// "Data Dictionary/ACA/forms". The jobs service reads the same file to print
// the PDF — see services/aca-dms-jobs/src/AcaDmsJobs/Forms/FormDefinition.cs —
// and checks values with the same rules as validateValue below.

import { evaluateFormula, FormulaNode, FormulaRef, FormulaScope, formulaRefs, tryParseFormula } from './forms.formula';

export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'money'
  | 'date'
  | 'select'
  | 'radio'
  | 'checkboxes'
  | 'checkbox'
  | 'email'
  | 'phone'
  | 'thaiId'
  | 'table'
  | 'paragraph'
  | 'calc'
  | 'signature'
  | 'address';

export type FormColumnType = 'text' | 'number' | 'money' | 'date' | 'calc';

/** filled, empty, equals, notEquals, includes (a choice ticked), greater, less. */
export type ConditionOp = 'filled' | 'empty' | 'equals' | 'notEquals' | 'includes' | 'greater' | 'less';

/** "Show this when <field> <op> <value>". */
export interface FormCondition {
  field: string;
  op: ConditionOp;
  value?: string;
}

/** How a calc field or column prints. */
export type CalcFormat = 'money' | 'number';

export type FormStatus = 'draft' | 'published' | 'archived';

export interface FormColumn {
  key: string;
  label: string;
  type: FormColumnType;
  /** calc: arithmetic over the other columns of the row */
  formula?: string;
  format?: CalcFormat;
}

export interface FormField {
  key: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  help?: string;
  placeholder?: string;
  /** In a two-column section, a half-width field shares its row. */
  width?: 'full' | 'half';
  /** select, radio and checkboxes */
  options?: string[];
  /** table */
  columns?: FormColumn[];
  /** paragraph */
  text?: string;
  /** The dms: property the value is copied into on the stored PDF. */
  metadata?: string;
  /** Asked (and printed) only when this holds. */
  showIf?: FormCondition;
  /** calc: see forms.formula.ts */
  formula?: string;
  format?: CalcFormat;
  /** signature: signs the sender's line in the signature boxes at the foot of the PDF, not in its section. */
  inFooter?: boolean;
  /** Where the field goes on the form's paper, when it has one. A table's places are filled one after another. */
  places?: FormPlace[];
}

export interface FormSection {
  id: string;
  title?: string;
  columns: 1 | 2;
  fields: FormField[];
  /** The whole section is asked (and printed) only when this holds. */
  showIf?: FormCondition;
}

/** The paper's look. The jobs service checks each value again (FormLook.cs). */
export interface FormStyle {
  /** A hex colour, e.g. #1d4ed8 */
  accent?: string;
  orientation?: 'portrait' | 'landscape';
  density?: 'normal' | 'compact';
  /** A PNG or JPEG data: URL. */
  logo?: string;
}

/** Where a submitted PDF goes and how it is filed. */
export interface FormOutput {
  /** Folder node id; empty = the person's own My Files. */
  folderId?: string;
  folderName?: string;
  documentType?: string;
  status?: string;
  confidentiality?: string;
  tags?: string[];
  /** Sign the filed PDF with the organisation's certificate, naming whoever submits it. The jobs service signs; the setting is read from the saved form. */
  sign?: boolean;
  /** The approval flow, a template in the workflow catalogue, that each filed PDF starts. */
  workflowId?: string;
  /** The flow's name when it was chosen, to say which one is meant once it has gone from the catalogue. */
  workflowName?: string;
}

/** What a place on the paper shows: FormPlaceParts in the jobs service. */
export type FormPlacePart = 'value' | 'words' | 'day' | 'month' | 'year' | 'option' | 'reference' | 'name' | 'date';

/**
 * A box on the paper, in points from the top-left corner of the page as it
 * shows (its crop box, turned the way it reads): what pdf.js draws at scale 1.
 */
export interface FormPlace {
  /** 1 for the first page. */
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  /** The value when not set. */
  part?: FormPlacePart;
  /** The choice an option place ticks for. */
  option?: string;
  /** Font size in points; the service makes it smaller when the text would not fit. */
  size?: number;
  align?: 'left' | 'center' | 'right';
  /** One character to each of this many boxes printed on the paper, such as a Thai ID's thirteen. */
  cells?: number;
  /** A table's row height on the paper, in points. */
  rowHeight?: number;
  /** A table's column widths, as shares of the place's width, in the columns' order. */
  widths?: number[];
}

/** The organisation's own paper form, which the values are printed onto instead of the standard layout. */
export interface FormPaper {
  /** The PDF's node, in the forms folder's "paper" folder. */
  nodeId: string;
  name?: string;
  /** Each page's size as pdf.js shows it, in points. */
  pages: { width: number; height: number }[];
  /** Places for what the service knows of each copy: its reference, the sender's name, the date and its parts. */
  marks: FormPlace[];
}

export interface FormDefinition {
  /** Node id of the JSON file — not stored inside it. */
  id?: string;
  /** Short code at the start of every reference number, e.g. PO. */
  code?: string;
  title: string;
  description?: string;
  /** Goes up each time a published form changes, so every PDF says which wording it was filled in on. */
  version: number;
  status: FormStatus;
  header?: { organisation?: string; subtitle?: string };
  footer?: { signatures: string[]; note?: string };
  sections: FormSection[];
  output?: FormOutput;
  style?: FormStyle;
  /** When set, the PDF is the values printed onto this paper. */
  paper?: FormPaper;
  /** Set the first time the form is published; from then on every change to its wording is a new version. */
  publishedAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** A change of status alone never changes the wording, so the version stays. */
export function withStatus(form: FormDefinition, status: FormStatus): FormDefinition {
  return { ...form, status, publishedAt: status === 'published' ? (form.publishedAt ?? new Date().toISOString()) : form.publishedAt };
}

/** The version a changed form is saved as: unchanged until it has been published, since no PDF names it yet. */
export const editedVersion = (form: FormDefinition): number => (form.publishedAt ? form.version + 1 : form.version);

/** An address field's value: the house and street as typed, then the area picked from the list (ThaiAddress.cs in the service). */
export interface FormAddress {
  line: string;
  subdistrict: string;
  district: string;
  province: string;
  postcode: string;
}

export type FormValue = string | number | boolean | string[] | Record<string, string | number>[] | FormAddress | null;
export type FormValues = Record<string, FormValue>;

/** Same codes as the jobs service's FormProblem. */
export type FormProblemCode =
  | 'required'
  | 'number'
  | 'date'
  | 'option'
  | 'email'
  | 'phone'
  | 'thaiId'
  | 'tooLong'
  | 'tooManyRows'
  | 'signature'
  | 'address';

export const MAX_TEXT = 500;
export const MAX_TEXTAREA = 5000;
export const MAX_ROWS = 200;
/** A table's row height on paper when the builder was not told: PaperLayer.DefaultRowHeight. */
export const PAPER_ROW_HEIGHT = 18;
/** A signature is a few strokes, cropped to them: FormLook.MaxSignatureBytes in the service. */
export const MAX_SIGNATURE_BYTES = 150_000;

/** The palette: each field type with its icon and tone. */
export const FIELD_TYPES: { type: FormFieldType; icon: string; tone: 'blue' | 'green' | 'purple' | 'amber' | 'red' }[] = [
  { type: 'text', icon: 'short_text', tone: 'blue' },
  { type: 'textarea', icon: 'notes', tone: 'blue' },
  { type: 'number', icon: 'pin', tone: 'green' },
  { type: 'money', icon: 'payments', tone: 'green' },
  { type: 'date', icon: 'event', tone: 'purple' },
  { type: 'select', icon: 'arrow_drop_down_circle', tone: 'amber' },
  { type: 'radio', icon: 'radio_button_checked', tone: 'amber' },
  { type: 'checkboxes', icon: 'checklist', tone: 'amber' },
  { type: 'checkbox', icon: 'check_box', tone: 'amber' },
  { type: 'email', icon: 'alternate_email', tone: 'blue' },
  { type: 'phone', icon: 'call', tone: 'blue' },
  { type: 'thaiId', icon: 'badge', tone: 'red' },
  { type: 'table', icon: 'table_rows', tone: 'purple' },
  { type: 'calc', icon: 'calculate', tone: 'green' },
  { type: 'signature', icon: 'draw', tone: 'purple' },
  { type: 'address', icon: 'home_pin', tone: 'blue' },
  { type: 'paragraph', icon: 'subject', tone: 'red' }
];

/** Accent colours offered for the paper; the first is the default. */
export const ACCENTS = ['#1f2937', '#1d4ed8', '#0f766e', '#15803d', '#b45309', '#b91c1c', '#7e22ce'];
export const MAX_LOGO_BYTES = 200_000;

/** dms: properties a field may fill, and the field types that suit each. */
export const METADATA_TARGETS: { property: string; types: FormFieldType[] }[] = [
  { property: 'dms:documentCode', types: ['text'] },
  { property: 'dms:documentDate', types: ['date'] },
  { property: 'dms:department', types: ['text', 'select', 'radio'] },
  { property: 'dms:amount', types: ['money', 'number', 'calc'] },
  { property: 'dms:documentOwner', types: ['text', 'select'] }
];

export const hasOptions = (type: FormFieldType): boolean => type === 'select' || type === 'radio' || type === 'checkboxes';

export const isNumericField = (field: FormField): boolean => field.type === 'number' || field.type === 'money' || field.type === 'calc';
export const isNumericColumn = (column: FormColumn): boolean => column.type === 'number' || column.type === 'money' || column.type === 'calc';
/** Money columns, and calc columns printed as money: these are added up under the table. */
export const isMoneyColumn = (column: FormColumn): boolean => column.type === 'money' || (column.type === 'calc' && column.format !== 'number');
/**
 * The money columns added up under a table. A column another column's formula
 * works from (a unit price in qty * price) is an input, not an amount: its total
 * would mean nothing. FormLogic.Totalled in the service.
 */
export function isTotalled(table: FormField, column: FormColumn): boolean {
  if (!isMoneyColumn(column)) {
    return false;
  }
  return !(table.columns ?? []).some((c) => {
    const node = c.type === 'calc' && c !== column ? tryParseFormula(c.formula) : null;
    return !!node && formulaRefs(node).some((r) => !r.member && r.name === column.key);
  });
}

/** How a calc value reads: as money unless it says number. */
export const printedType = (item: { type: string; format?: CalcFormat }): string =>
  item.type === 'calc' ? (item.format === 'number' ? 'number' : 'money') : item.type;

/** Fields that always take the whole width: a table, long text, a list of choices, an address's four boxes. */
export const isWideType = (type: FormFieldType): boolean => ['table', 'textarea', 'paragraph', 'checkboxes', 'address'].includes(type);

/** Paragraphs show text; every other field takes a value. */
export const takesValue = (field: FormField): boolean => field.type !== 'paragraph';

export function newForm(): FormDefinition {
  return {
    title: '',
    version: 1,
    status: 'draft',
    header: { organisation: '', subtitle: '' },
    footer: { signatures: [], note: '' },
    sections: [newSection(1)],
    output: { documentType: 'form', status: 'pending', confidentiality: 'internal', tags: [] }
  };
}

export function newSection(index: number): FormSection {
  return { id: `s${Date.now().toString(36)}${index}`, title: '', columns: 2, fields: [] };
}

/** A field of the given type with a key no other field of the form uses. */
export function newField(type: FormFieldType, form: FormDefinition, label: string): FormField {
  const taken = new Set(allFields(form).map((f) => f.key));
  let n = 1;
  while (taken.has(`${type}${n}`)) {
    n++;
  }
  const field: FormField = { key: `${type}${n}`, label, type, required: false, width: 'half' };
  if (hasOptions(type)) {
    field.options = ['1', '2'];
  }
  if (type === 'table') {
    field.width = 'full';
    field.columns = [
      { key: 'item', label: '', type: 'text' },
      { key: 'amount', label: '', type: 'money' }
    ];
  }
  if (type === 'textarea' || type === 'paragraph' || type === 'checkboxes' || type === 'checkbox' || type === 'address') {
    field.width = 'full';
  }
  if (type === 'paragraph') {
    field.text = label;
  }
  if (type === 'calc') {
    field.formula = '';
    field.format = 'money';
  }
  return field;
}

export const allFields = (form: FormDefinition): FormField[] => form.sections.flatMap((s) => s.fields);

/** A key safe to use as a JSON property: letters, digits and underscores, starting with a letter. */
export function cleanKey(key: string): string {
  const clean = (key || '').replace(/[^A-Za-z0-9_]/g, '');
  return /^[A-Za-z]/.test(clean) ? clean : `f${clean}`;
}

/** Problems a form definition has before it can be published. */
export function definitionProblems(form: FormDefinition): string[] {
  const problems: string[] = [];
  if (!form.title.trim()) {
    problems.push('NO_TITLE');
  }
  const fields = allFields(form);
  if (!fields.some(takesValue)) {
    problems.push('NO_FIELDS');
  }
  const keys = fields.map((f) => f.key);
  if (new Set(keys).size !== keys.length) {
    problems.push('DUPLICATE_KEYS');
  }
  if (fields.some((f) => takesValue(f) && f.type !== 'checkbox' && !f.label.trim())) {
    problems.push('NO_LABEL');
  }
  if (fields.some((f) => hasOptions(f.type) && !(f.options ?? []).some((o) => o.trim()))) {
    problems.push('NO_OPTIONS');
  }
  if (fields.some((f) => f.type === 'table' && !(f.columns ?? []).some((c) => c.label.trim()))) {
    problems.push('NO_COLUMNS');
  }
  if (fields.some((f) => formulaProblem(form, f) !== null || (f.columns ?? []).some((c) => columnFormulaProblem(f, c) !== null))) {
    problems.push('BAD_FORMULA');
  }
  const owned: { condition?: FormCondition; owner?: FormField }[] = [
    ...form.sections.map((s) => ({ condition: s.showIf })),
    ...fields.map((f) => ({ condition: f.showIf, owner: f }))
  ];
  if (owned.some(({ condition, owner }) => condition && conditionProblem(form, condition, owner) !== null)) {
    problems.push('BAD_CONDITION');
  }
  if (hasLoop(form)) {
    problems.push('LOOP');
  }
  if (fields.filter((f) => f.type === 'signature' && f.inFooter).length > 1) {
    problems.push('TWO_SENDER_SIGNATURES');
  }
  // the blank paper would come out, whatever was filled in
  if (form.paper && !form.paper.marks?.length && !fields.some((f) => takesValue(f) && f.places?.length)) {
    problems.push('PAPER_NOTHING_PLACED');
  }
  return problems;
}

export interface FormulaProblem {
  code: 'NOT_ARITHMETIC' | 'UNKNOWN_NAME';
  name?: string;
}

const refName = (ref: FormulaRef): string => (ref.member ? ref.name + '.' + ref.member : ref.name);

/** What is wrong with a calc field's formula, if anything: not arithmetic, or a name that is not a number field, a table column or a counted table. */
export function formulaProblem(form: FormDefinition, field: FormField): FormulaProblem | null {
  if (field.type !== 'calc') {
    return null;
  }
  const node = tryParseFormula(field.formula);
  if (!node) {
    return { code: 'NOT_ARITHMETIC' };
  }
  const byKey = new Map(allFields(form).map((f) => [f.key, f]));
  for (const ref of formulaRefs(node)) {
    const target = byKey.get(ref.name);
    let ok = false;
    if (target && isCounted(node, ref)) {
      ok = target.type === 'table' && !ref.member;
    } else if (target && ref.member) {
      ok = target.type === 'table' && (target.columns ?? []).some((c) => c.key === ref.member && isNumericColumn(c));
    } else if (target) {
      ok = isNumericField(target);
    }
    if (!ok) {
      return { code: 'UNKNOWN_NAME', name: refName(ref) };
    }
  }
  return null;
}

/** A calc column may read only the other number columns of its row. */
export function columnFormulaProblem(table: FormField, column: FormColumn): FormulaProblem | null {
  if (column.type !== 'calc') {
    return null;
  }
  const node = tryParseFormula(column.formula);
  if (!node) {
    return { code: 'NOT_ARITHMETIC' };
  }
  for (const ref of formulaRefs(node)) {
    const target = (table.columns ?? []).find((c) => c.key === ref.name);
    if (ref.member || !target || target === column || !isNumericColumn(target)) {
      return { code: 'UNKNOWN_NAME', name: refName(ref) };
    }
  }
  return null;
}

function isCounted(node: FormulaNode, ref: FormulaRef): boolean {
  switch (node.kind) {
    case 'call':
      return (node.name === 'count' && node.args.includes(ref)) || node.args.some((a) => isCounted(a, ref));
    case 'negate':
      return isCounted(node.operand, ref);
    case 'binary':
      return isCounted(node.left, ref) || isCounted(node.right, ref);
    default:
      return false;
  }
}

/** A condition must name another field that takes a value, and carry a value when its test compares with one. */
export function conditionProblem(form: FormDefinition, condition: FormCondition, owner?: FormField): 'NO_FIELD' | 'NO_VALUE' | null {
  const source = allFields(form).find((f) => f.key === condition.field);
  if (!source || source === owner || source.type === 'paragraph' || source.type === 'table') {
    return 'NO_FIELD';
  }
  const needsValue = condition.op !== 'filled' && condition.op !== 'empty';
  if (needsValue && !(condition.value ?? '').trim()) {
    return 'NO_VALUE';
  }
  if ((condition.op === 'greater' || condition.op === 'less') && toNumber(condition.value ?? '') === null) {
    return 'NO_VALUE';
  }
  return null;
}

/**
 * Whether a field's showing or its sum comes back round to itself: a field
 * shown when a field in its own section is filled while that section depends
 * on it, a total that adds itself. The service would leave it blank and hidden.
 */
export function hasLoop(form: FormDefinition): boolean {
  const deps = new Map<string, string[]>();
  for (const section of form.sections) {
    for (const field of section.fields) {
      const on = [section.showIf?.field, field.showIf?.field].filter((k): k is string => !!k);
      if (field.type === 'calc') {
        const node = tryParseFormula(field.formula);
        on.push(...(node ? formulaRefs(node).map((r) => r.name) : []));
      }
      if ((field.columns ?? []).some((c) => c.type === 'calc' && columnLoop(field, c))) {
        return true;
      }
      deps.set(field.key, on);
    }
  }
  const state = new Map<string, 'open' | 'done'>();
  const visit = (key: string): boolean => {
    if (state.get(key) === 'open') {
      return true;
    }
    if (state.get(key) === 'done' || !deps.has(key)) {
      return false;
    }
    state.set(key, 'open');
    const looped = (deps.get(key) ?? []).some(visit);
    state.set(key, 'done');
    return looped;
  };
  return [...deps.keys()].some(visit);
}

function columnLoop(table: FormField, start: FormColumn): boolean {
  const open = new Set<string>();
  const walk = (column: FormColumn): boolean => {
    if (open.has(column.key)) {
      return true;
    }
    open.add(column.key);
    const node = tryParseFormula(column.formula);
    const next = (node ? formulaRefs(node) : [])
      .map((r) => (table.columns ?? []).find((c) => c.key === r.name && c.type === 'calc'))
      .filter((c): c is FormColumn => !!c);
    const looped = next.some(walk);
    open.delete(column.key);
    return looped;
  };
  return walk(start);
}

// ── checking a value (the jobs service's FormValues.cs, line for line) ───

export function isEmpty(value: FormValue | undefined): boolean {
  if (value === null || value === undefined || value === false) {
    return true;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  if (isAddress(value)) {
    return ADDRESS_PARTS.every((part) => !String(value[part] ?? '').trim());
  }
  return Array.isArray(value) && value.length === 0;
}

const ADDRESS_PARTS: (keyof FormAddress)[] = ['line', 'subdistrict', 'district', 'province', 'postcode'];

export const isAddress = (value: FormValue | undefined): value is FormAddress => !!value && typeof value === 'object' && !Array.isArray(value);

export const emptyAddress = (): FormAddress => ({ line: '', subdistrict: '', district: '', province: '', postcode: '' });

export const BANGKOK = 'กรุงเทพมหานคร';

/**
 * An address on one line, as a Thai letter writes it (ThaiAddress.Format):
 * 99/1 หมู่ 2 ตำบลบางพลีใหญ่ อำเภอบางพลี จังหวัดสมุทรปราการ 10540; in Bangkok
 * แขวง… เขต… กรุงเทพมหานคร.
 */
export function formatAddress(address: FormAddress): string {
  const bangkok = address.province?.trim() === BANGKOK;
  const parts: [string, string | undefined][] = [
    ['', address.line],
    [bangkok ? 'แขวง' : 'ตำบล', address.subdistrict],
    [bangkok ? 'เขต' : 'อำเภอ', address.district],
    [bangkok ? '' : 'จังหวัด', address.province],
    ['', address.postcode]
  ];
  return parts
    .map(([prefix, part]) => [prefix, (part ?? '').trim()])
    .filter(([, part]) => part)
    .map(([prefix, part]) => prefix + part)
    .join(' ');
}

/**
 * All of it or none of it: the house and street, a subdistrict, district and
 * province (the picker only offers real ones; the service checks the list
 * again), and a five-digit postcode.
 */
function validateAddress(value: FormValue | undefined): FormProblemCode | null {
  if (!isAddress(value) || ADDRESS_PARTS.some((part) => typeof (value[part] ?? '') !== 'string')) {
    return 'address';
  }
  if ((['line', 'subdistrict', 'district', 'province'] as const).some((part) => (value[part] ?? '').trim().length > MAX_TEXT)) {
    return 'tooLong';
  }
  const missing = (['line', 'subdistrict', 'district', 'province'] as const).some((part) => !(value[part] ?? '').trim());
  return missing || !/^[1-9]\d{4}$/.test((value.postcode ?? '').trim()) ? 'address' : null;
}

export function toNumber(value: FormValue | undefined): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const text = value.replace(/,/g, '').trim();
  return /^[+-]?(\d+\.?\d*|\.\d+)$/.test(text) ? Number(text) : null;
}

export const isIsoDate = (value: FormValue | undefined): boolean =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(new Date(`${value}T00:00:00`).getTime());

export function isThaiId(value: string): boolean {
  const digits = (value || '').replace(/[- ]/g, '');
  if (!/^\d{13}$/.test(digits)) {
    return false;
  }
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += Number(digits[i]) * (13 - i);
  }
  return (11 - (sum % 11)) % 10 === Number(digits[12]);
}

/** A PNG data: URL of a sensible size, as the signature pad makes it; FormLook.SignatureImage in the service. */
export function isSignatureImage(value: FormValue | undefined): boolean {
  const match = typeof value === 'string' ? /^data:image\/png;base64,([A-Za-z0-9+/]+={0,2})$/.exec(value) : null;
  if (!match || match[1].length % 4 !== 0 || !match[1].startsWith('iVBORw0KGgo')) {
    return false;
  }
  const padding = match[1].endsWith('==') ? 2 : match[1].endsWith('=') ? 1 : 0;
  return (match[1].length / 4) * 3 - padding <= MAX_SIGNATURE_BYTES;
}

/** How many table rows fit in the field's places on the paper, or null when the paper does not limit them: PaperLayer.RowsOnPaper. */
export function paperRows(form: FormDefinition, table: FormField): number | null {
  if (!form.paper || table.type !== 'table') {
    return null;
  }
  const places = (table.places ?? []).filter((p) => (p.part ?? 'value') === 'value');
  if (!places.length) {
    return null;
  }
  return places.reduce((sum, p) => sum + Math.floor(Math.max(p.h, 0) / Math.min(Math.max(p.rowHeight ?? PAPER_ROW_HEIGHT, 8), 200)), 0);
}

/** The most rows a table takes: what its places on the paper hold, when the form has a paper. */
export const maxRows = (form: FormDefinition, table: FormField): number => Math.min(paperRows(form, table) ?? MAX_ROWS, MAX_ROWS);

export function validateValue(field: FormField, value: FormValue | undefined, rowLimit = MAX_ROWS): FormProblemCode | null {
  // a calc field is worked out, not typed
  if (!takesValue(field) || field.type === 'calc') {
    return null;
  }
  if (isEmpty(value)) {
    return field.required ? 'required' : null;
  }
  const text = typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
  switch (field.type) {
    case 'number':
    case 'money':
      return toNumber(value) === null ? 'number' : null;
    case 'date':
      return isIsoDate(value) ? null : 'date';
    case 'select':
    case 'radio':
      return (field.options ?? []).includes(text) ? null : 'option';
    case 'checkboxes':
      return Array.isArray(value) && (value as string[]).every((v) => (field.options ?? []).includes(v)) ? null : 'option';
    case 'checkbox':
      return typeof value === 'boolean' ? null : 'option';
    case 'email':
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(text) ? null : 'email';
    case 'phone':
      return /^\+?[0-9][0-9 -]{7,14}$/.test(text) ? null : 'phone';
    case 'thaiId':
      return isThaiId(text) ? null : 'thaiId';
    case 'textarea':
      return text.length > MAX_TEXTAREA ? 'tooLong' : null;
    case 'table':
      return validateRows(field, value, rowLimit);
    case 'signature':
      return isSignatureImage(value) ? null : 'signature';
    case 'address':
      return validateAddress(value);
    default:
      return text.length > MAX_TEXT ? 'tooLong' : null;
  }
}

function validateRows(field: FormField, value: FormValue | undefined, rowLimit: number): FormProblemCode | null {
  if (!Array.isArray(value)) {
    return 'option';
  }
  const rows = value as Record<string, string | number>[];
  // blank rows do not count against the paper's lines
  if (rows.filter((row) => hasTyping(field, row)).length > rowLimit || rows.length > MAX_ROWS) {
    return 'tooManyRows';
  }
  for (const row of rows) {
    for (const column of (field.columns ?? []).filter((c) => c.type !== 'calc')) {
      const cell = row?.[column.key];
      if (isEmpty(cell)) {
        continue;
      }
      if ((column.type === 'number' || column.type === 'money') && toNumber(cell) === null) {
        return 'number';
      }
      if (column.type === 'date' && !isIsoDate(cell)) {
        return 'date';
      }
    }
  }
  return null;
}

/** Every field that is asked: fields the form's conditions hide are left out, as the service leaves them out. */
export function validateAll(form: FormDefinition, values: FormValues): Record<string, FormProblemCode> {
  const state = prepare(form, values);
  const problems: Record<string, FormProblemCode> = {};
  for (const field of allFields(form).filter((f) => !state.hidden.has(f.key))) {
    const problem = validateValue(field, state.values[field.key], maxRows(form, field));
    if (problem) {
      problems[field.key] = problem;
    }
  }
  return problems;
}

// ── the form's own rules (the jobs service's FormLogic.cs, line for line) ─

/** The values as the form's rules leave them, and what those rules hide. */
export interface FormState {
  values: FormValues;
  hidden: Set<string>;
  hiddenSections: Set<FormSection>;
}

type Row = Record<string, string | number>;

/** A row with something typed into it; a calc column alone does not count. */
export const hasTyping = (table: FormField, row: Row | undefined): boolean =>
  (table.columns ?? []).some((c) => c.type !== 'calc' && !isEmpty(row?.[c.key]));

/**
 * Leaves out fields and sections whose condition fails and works out calc
 * fields and columns. A loop in the rules (which the builder refuses) reads as
 * hidden and blank, as it does in the service.
 */
export function prepare(form: FormDefinition, raw: FormValues): FormState {
  const fields = new Map<string, { field: FormField; section: FormSection }>();
  for (const section of form.sections) {
    for (const field of section.fields) {
      if (!fields.has(field.key)) {
        fields.set(field.key, { field, section });
      }
    }
  }
  const hiddenMemo = new Map<string, boolean>();
  const valueMemo = new Map<string, FormValue | undefined>();
  const visiting = new Set<string>();

  const holds = (condition: FormCondition | undefined): boolean => {
    if (!condition?.field?.trim() || !fields.has(condition.field)) {
      return true;
    }
    const value = valueOf(condition.field);
    const wanted = (condition.value ?? '').trim();
    switch (condition.op) {
      case 'empty':
        return isEmpty(value);
      case 'equals':
        return same(value, wanted);
      case 'notEquals':
        return !same(value, wanted);
      case 'includes':
        return Array.isArray(value) ? (value as unknown[]).includes(wanted) : same(value, wanted);
      case 'greater':
      case 'less': {
        const number = toNumber(value);
        const limit = toNumber(wanted);
        if (number === null || limit === null) {
          return false;
        }
        return condition.op === 'greater' ? number > limit : number < limit;
      }
      default:
        return !isEmpty(value);
    }
  };

  const hidden = (key: string): boolean => {
    const memo = hiddenMemo.get(key);
    if (memo !== undefined) {
      return memo;
    }
    const entry = fields.get(key);
    if (!entry || visiting.has('h:' + key)) {
      return true;
    }
    visiting.add('h:' + key);
    const result = !holds(entry.section.showIf) || !holds(entry.field.showIf);
    visiting.delete('h:' + key);
    hiddenMemo.set(key, result);
    return result;
  };

  const fieldScope: FormulaScope = {
    value: (ref) => {
      const entry = fields.get(ref.name);
      // a formula that comes back round to itself has no answer
      if (!entry || visiting.has('v:' + ref.name)) {
        return null;
      }
      const field = entry.field;
      if (!ref.member) {
        return isNumericField(field) ? (toNumber(valueOf(field.key)) ?? 0) : null;
      }
      const column = (field.columns ?? []).find((c) => c.key === ref.member);
      if (field.type !== 'table' || !column || !isNumericColumn(column)) {
        return null;
      }
      return columnTotal((valueOf(field.key) as Row[] | undefined) ?? [], column.key);
    },
    count: (ref) => {
      const entry = fields.get(ref.name);
      if (!entry || entry.field.type !== 'table') {
        return null;
      }
      const rows = valueOf(entry.field.key);
      return Array.isArray(rows) ? (rows as Row[]).filter((row) => hasTyping(entry.field, row)).length : 0;
    }
  };

  const valueOf = (key: string): FormValue | undefined => {
    if (valueMemo.has(key)) {
      return valueMemo.get(key);
    }
    const entry = fields.get(key);
    if (!entry || hidden(key) || visiting.has('v:' + key)) {
      return undefined;
    }
    visiting.add('v:' + key);
    const field = entry.field;
    let value: FormValue | undefined = raw[key];
    if (field.type === 'calc') {
      const node = tryParseFormula(field.formula);
      value = node ? (evaluateFormula(node, fieldScope) ?? undefined) : undefined;
    } else if (field.type === 'table' && (field.columns ?? []).some((c) => c.type === 'calc')) {
      value = Array.isArray(value) ? (value as Row[]).map((row) => withCalcColumns(field, row)) : value;
    }
    visiting.delete('v:' + key);
    valueMemo.set(key, value);
    return value;
  };

  const values: FormValues = {};
  const hiddenKeys = new Set<string>();
  for (const [key, { field }] of fields) {
    if (field.type === 'paragraph') {
      continue;
    }
    if (hidden(key)) {
      hiddenKeys.add(key);
    } else {
      const value = valueOf(key);
      if (value !== undefined) {
        values[key] = value;
      }
    }
  }
  return { values, hidden: hiddenKeys, hiddenSections: new Set(form.sections.filter((s) => !holds(s.showIf))) };
}

/** Numbers compare as numbers (1,000 is 1000); anything else as its text, trimmed. */
function same(value: FormValue | undefined, wanted: string): boolean {
  if (isEmpty(value) && value !== false) {
    return wanted.length === 0;
  }
  const number = toNumber(value);
  const other = toNumber(wanted);
  if (number !== null && other !== null) {
    return number === other;
  }
  return (isAddress(value) ? formatAddress(value) : String(value ?? '')).trim() === wanted;
}

function withCalcColumns(table: FormField, source: Row): Row {
  const row: Row = { ...source };
  const calcs = (table.columns ?? []).filter((c) => c.type === 'calc');
  for (const column of calcs) {
    delete row[column.key];
  }
  if (!hasTyping(table, row)) {
    return row;
  }
  const done = new Map<string, number | null>();
  const working = new Set<string>();
  const calc = (column: FormColumn): number | null => {
    if (done.has(column.key)) {
      return done.get(column.key) ?? null;
    }
    if (working.has(column.key)) {
      return null;
    }
    working.add(column.key);
    const node = tryParseFormula(column.formula);
    const result = node ? evaluateFormula(node, scope) : null;
    working.delete(column.key);
    done.set(column.key, result);
    return result;
  };
  const scope: FormulaScope = {
    value: (ref) => {
      const column = (table.columns ?? []).find((c) => c.key === ref.name);
      if (ref.member || !column || !isNumericColumn(column)) {
        return null;
      }
      return column.type === 'calc' ? calc(column) : (toNumber(row[column.key]) ?? 0);
    },
    count: () => null
  };
  for (const column of calcs) {
    const result = calc(column);
    if (result !== null) {
      row[column.key] = result;
    }
  }
  return row;
}

/** A blank set of values: a table starts with one empty row to type into. */
export function emptyValues(form: FormDefinition): FormValues {
  const values: FormValues = {};
  for (const field of allFields(form).filter(takesValue)) {
    values[field.key] =
      field.type === 'table'
        ? [emptyRow(field)]
        : field.type === 'checkboxes'
          ? []
          : field.type === 'checkbox'
            ? false
            : field.type === 'address'
              ? emptyAddress()
              : '';
  }
  return values;
}

export const emptyRow = (field: FormField): Record<string, string> => Object.fromEntries((field.columns ?? []).map((c) => [c.key, '']));

/** The sum of a money column, ignoring cells that are not numbers. */
export const columnTotal = (rows: Record<string, string | number>[], key: string): number =>
  rows.reduce((sum, row) => sum + (toNumber(row?.[key]) ?? 0), 0);

/**
 * Properties for the stored PDF: the form's own filing (type, status,
 * confidentiality), the reference number as the document code, and every
 * field mapped to a dms: property.
 */
export function storedProperties(form: FormDefinition, entered: FormValues, reference: string, today: string): Record<string, unknown> {
  // what the PDF printed: hidden fields left out, calc fields worked out
  const values = prepare(form, entered).values;
  const properties: Record<string, unknown> = {
    'cm:title': form.title,
    'cm:description': reference,
    'dms:documentCode': reference,
    'dms:documentDate': today,
    'dms:documentType': form.output?.documentType || 'form',
    'dms:status': form.output?.status || 'pending',
    'dms:confidentiality': form.output?.confidentiality || 'internal'
  };
  for (const field of allFields(form)) {
    const value = values[field.key];
    if (!field.metadata || isEmpty(value)) {
      continue;
    }
    properties[field.metadata] = field.metadata === 'dms:amount' ? toNumber(value) : value;
  }
  return properties;
}

/**
 * What a flow started by a filed form can route on: the properties the PDF
 * was filed with, and each answer under its field key as printed (numbers as
 * numbers, choices joined, an address on one line; tables, signatures and
 * hidden fields left out).
 */
export function workflowValues(form: FormDefinition, entered: FormValues, reference: string, today: string): Record<string, unknown> {
  const values = prepare(form, entered).values;
  const result: Record<string, unknown> = {};
  for (const field of allFields(form)) {
    const value = values[field.key];
    // a signature is a picture: the flow gets the PDF it is printed on
    if (field.type === 'table' || field.type === 'paragraph' || field.type === 'signature' || isEmpty(value)) {
      continue;
    }
    if (Array.isArray(value)) {
      result[field.key] = value.join(', ');
    } else if (isAddress(value)) {
      result[field.key] = formatAddress(value);
    } else if (field.type === 'number' || field.type === 'money') {
      result[field.key] = toNumber(value);
    } else {
      result[field.key] = value;
    }
  }
  return { ...result, ...storedProperties(form, entered, reference, today) };
}

// ── amounts in words (ThaiFormat.BahtText in the jobs service) ──────────

const DIGITS = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
const PLACES = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน'];

function group(n: number, higher: boolean): string {
  const digits = String(n);
  let text = '';
  for (let i = 0; i < digits.length; i++) {
    const digit = Number(digits[i]);
    const place = digits.length - 1 - i;
    if (digit === 0) {
      continue;
    }
    if (place === 0 && digit === 1 && (n > 9 || higher)) {
      text += 'เอ็ด';
    } else if (place === 1 && digit === 1) {
      text += 'สิบ';
    } else if (place === 1 && digit === 2) {
      text += 'ยี่สิบ';
    } else {
      text += DIGITS[digit] + PLACES[place];
    }
  }
  return text;
}

function words(n: number): string {
  if (n === 0) {
    return '';
  }
  const millions = Math.floor(n / 1_000_000);
  return (millions > 0 ? words(millions) + 'ล้าน' : '') + group(n % 1_000_000, millions > 0);
}

/** หนึ่งแสนห้าหมื่นบาทถ้วน — the way a cheque or a quotation writes an amount. */
export function bahtText(amount: number): string {
  const satangTotal = Math.round(Math.abs(amount) * 100);
  const baht = Math.floor(satangTotal / 100);
  const satang = satangTotal % 100;
  let text: string;
  if (baht === 0 && satang === 0) {
    text = 'ศูนย์บาทถ้วน';
  } else if (satang === 0) {
    text = words(baht) + 'บาทถ้วน';
  } else {
    text = (baht > 0 ? words(baht) + 'บาท' : '') + words(satang) + 'สตางค์';
  }
  return amount < 0 ? 'ลบ' + text : text;
}

/** "ใบขออนุมัติจัดซื้อ PO-25691024-1430-7K.pdf", without characters a file name may not hold. */
export function pdfName(form: FormDefinition, reference: string): string {
  const title = (form.title || 'form')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  return `${title} ${reference}.pdf`;
}
